import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import DynamicFeedRoundedIcon from '@mui/icons-material/DynamicFeedRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ShortcutRoundedIcon from '@mui/icons-material/ShortcutRounded';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import { accountsActions, accountsSelectors, accountsTypes } from 'app-components/flux/accounts';
import { businessActions, businessSelectors, businessTypes } from 'app-components/flux/business';
import type { Business } from 'app-components/flux/business/businessTypes';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import { QDropdownMenu, QMenuDivider, QMenuItem } from 'components/QDropdowns';
import { useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap } from 'immutable';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

type Props = {
  business: Business;
  setEditBusiness: (value: boolean) => void;
};

export default function BusinessThreeDot(props: Props) {
  const { business, setEditBusiness } = props;
  const businessId = business.id;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const defaultBusiness = useAppSelector(businessSelectors.getDefaultBusiness);
  const nonDefaultBusinesses = useAppSelector(businessSelectors.getNonDefaultBusinesses);

  const isDefaultBusiness = business.id === defaultBusiness?.id;

  const accountsById = useAppSelector(accountsSelectors.getAccountsById);

  // @ts-expect-error FIXME TS7006
  const buildBusinessUpdate = (biz, isClosed, isDefault) =>
    biz.has('isDefault')
      ? biz.merge({ isClosed, isDefault })
      : businessTypes.mkBusiness({ ...biz?.toJS(), isDefault, isClosed });

  // @ts-expect-error FIXME TS7006
  const updatePrimary = (newPrimary, shouldCloseDefault = false) => {
    const newPrimaryModified = buildBusinessUpdate(newPrimary, false, true);
    if (defaultBusiness) {
      const oldPrimary = buildBusinessUpdate(defaultBusiness, shouldCloseDefault, false);
      dispatch(businessActions.batchUpdateBusinesses([newPrimaryModified, oldPrimary]));
    } else {
      dispatch(businessActions.updateBusiness(newPrimaryModified));
    }
  };
  const showCloseBusiness = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'closedBusiness'));

  const handleEdit = () => {
    setEditBusiness(true);
  };

  const handleManageAccounts = () => {
    navigate('/settings/accounts', { replace: true });
  };

  const handleSetPrimary = () => {
    updatePrimary(business);
  };

  const handleCloseBusiness = () => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'close-business',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Close business',
              content: (
                <span>
                  Are you sure you sure you want to close {business?.name}? <br /> <br />
                  While your existing transactions will be preserved and linked with this business, no new transactions
                  will be associated with this business. <br /> <br />
                  All associated accounts will have their primary usage set to Personal.
                </span>
              ),
              confirmLabel: 'Confirm',
              denyLabel: 'Cancel',
              confirmAction: () => {
                accountsById?.forEach((account) => {
                  if (account.businessId === businessId) {
                    dispatch(
                      accountsActions.updateAccount(
                        accountsTypes.mkUpdateAccount(account.type, {
                          id: account.id,
                          usageType: 'PERSONAL',
                          businessId: '0',
                        }),
                      ),
                    );
                  }
                });
                if (isDefaultBusiness && nonDefaultBusinesses && nonDefaultBusinesses?.size > 0) {
                  updatePrimary(nonDefaultBusinesses.first(), true);
                } else {
                  dispatch(businessActions.updateBusiness(buildBusinessUpdate(business, true, false)));
                }
              },
            }),
          ),
        }),
      ),
    );
  };

  const handleReopenBusiness = () => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'reopen-business',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Reopen business',
              content: (
                <span>
                  Are you sure you sure you want to reopen {business?.name}? <br /> <br />
                  When reopened, go to the Accounts page in Settings to choose the accounts you’d like to associate with
                  the business.
                </span>
              ),
              confirmLabel: 'Confirm',
              denyLabel: 'Cancel',
              confirmAction: () => {
                if (nonDefaultBusinesses?.size === 0 || nonDefaultBusinesses === null) {
                  dispatch(businessActions.updateBusiness(buildBusinessUpdate(business, false, true)));
                } else {
                  dispatch(businessActions.updateBusiness(business.set('isClosed', false)));
                }
              },
            }),
          ),
        }),
      ),
    );
  };

  const handleDeleteBusiness = () => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-business',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Delete business',
              content: <span>Are you sure you sure you want to delete {business?.name}?</span>,
              confirmLabel: 'Confirm',
              denyLabel: 'Cancel',
              confirmAction: () => {
                dispatch(businessActions.deleteBusiness(business));
              },
            }),
          ),
        }),
      ),
    );
  };

  const openBusinessItems = [
    <QMenuItem
      key='edit'
      variant='action'
      id='edit-biz-accounts'
      label='Edit business'
      startIcon={<EditRoundedIcon fontSize='small' />}
      onClick={handleEdit}
    />,
    <QMenuDivider key='edit-divider' />,
    <QMenuItem
      key='manage'
      variant='action'
      id='manage-business-accts'
      label='Manage business accounts'
      startIcon={<ShortcutRoundedIcon fontSize='small' />}
      onClick={handleManageAccounts}
    />,
    <QMenuDivider key='manage-divider' />,
    ...(!isDefaultBusiness
      ? [
          <QMenuItem
            key='primary'
            variant='action'
            id='update-to-primary'
            label='Set as primary business'
            subtext='Updating your primary business will change all of your report views to show this business first.'
            startIcon={<SwapVertRoundedIcon fontSize='small' />}
            onClick={handleSetPrimary}
          />,
          ...(showCloseBusiness ? [<QMenuDivider key='primary-divider' />] : []),
        ]
      : []),
    ...(showCloseBusiness
      ? [
          <QMenuItem
            key='close'
            variant='action'
            id='close-biz'
            label='Close business'
            destructive
            startIcon={<DeleteRoundedIcon fontSize='small' />}
            onClick={handleCloseBusiness}
          />,
        ]
      : []),
  ];

  const closedBusinessItems = [
    ...(showCloseBusiness
      ? [
          <QMenuItem
            key='reopen'
            variant='action'
            id='reopen-biz'
            label='Reopen business'
            startIcon={<DynamicFeedRoundedIcon fontSize='small' />}
            onClick={handleReopenBusiness}
          />,
          <QMenuDivider key='reopen-divider' />,
        ]
      : []),
    <QMenuItem
      key='edit-closed'
      variant='action'
      id='edit-biz-accounts'
      label='Edit business'
      startIcon={<EditRoundedIcon fontSize='small' />}
      onClick={handleEdit}
    />,
    ...(showCloseBusiness
      ? [
          <QMenuDivider key='edit-closed-divider' />,
          <QMenuItem
            key='delete'
            variant='action'
            id='delete-biz'
            label='Delete business'
            destructive
            startIcon={<DeleteRoundedIcon fontSize='small' />}
            onClick={handleDeleteBusiness}
          />,
        ]
      : []),
  ];

  return (
    <div
      role='none'
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      <QDropdownMenu
        id={`business-three-dot-menu-${businessId}`}
        aria-label='Business options'
        iconButtonSize='small'
        iconButtonShape='rounded'
        width={280}
      >
        {business?.isClosed ? closedBusinessItems : openBusinessItems}
      </QDropdownMenu>
    </div>
  );
}
